import { motion } from "framer-motion";
import { Link, useSearch } from "wouter";
import {
  useAppNavigation,
  useIsNative,
  usePlatformNavigation,
} from "@/lib/platform";
import {
  MapPin,
  Users,
  ExternalLink,
  MessageCircle,
  CheckCircle,
  Check,
  Loader2,
  Edit,
  X,
  Hourglass,
  CalendarPlus,
  ChevronDown,
  Download,
  Handshake,
  Beer,
} from "lucide-react";
import { UserAPI } from "@shared/types/api";
import {
  formatTimeToDisplay,
  formatDate,
  DateFormats,
  parseUTCDate,
} from "@/utils/dateUtils";
import { getEventTypeLabel, getEventTypeEmoji } from "@/utils/eventTypeUtils";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import {
  useConfirmReservation,
  useUpdateReservation,
  useTablemates,
} from "@/lib/api";
import { CancellationModal, CancellationReason } from "./CancellationModal";
import { useToast } from "@/components/ux/Toast";
import { DateBadge } from "@/components/ux/DateBadge";
import tracking from "@/lib/tracking";
import { CANCELLATION_REFUND_WINDOW_HOURS } from "@shared/constants";
import { useT } from "@/lib/i18n";
import { ConnectionsEditor } from "./ConnectionsEditor";
import { InvitePlusOneSection } from "./InvitePlusOneSection";
import { usePlusOneInvites } from "@/lib/features";

interface ReservationCardProps {
  reservation: UserAPI.Reservation;
  onCancelSuccess?: () => void;
  clickable?: boolean;
  allowAutoConfirm?: boolean;
  expanded?: boolean;
}

export function ReservationCard({
  reservation,
  onCancelSuccess,
  clickable = true,
  allowAutoConfirm = false,
  expanded = false,
}: ReservationCardProps) {
  const { t } = useT();
  const [openMenu, setOpenMenu] = useState(false);
  const [openCalendarMenu, setOpenCalendarMenu] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const calendarMenuRef = useRef<HTMLDivElement>(null);
  const { navigate } = useAppNavigation();
  const isNative = useIsNative();
  const platformNavigation = usePlatformNavigation();

  // Inside the native shell the WebView can neither open target="_blank"
  // windows nor download .ics attachments, so calendar links must be handed
  // to the OS browser via the bridge.
  const openCalendarLink = useCallback(
    (e: React.MouseEvent, url: string, kind: "google" | "ics") => {
      e.stopPropagation();
      setOpenCalendarMenu(false);
      tracking.trackCalendarLinkClicked(kind);
      if (isNative) {
        e.preventDefault();
        platformNavigation.openExternal(
          new URL(url, window.location.origin).toString(),
        );
      }
    },
    [isNative, platformNavigation],
  );

  const confirmReservation = useConfirmReservation();
  const updateReservation = useUpdateReservation();
  const toast = useToast();
  const plusOneInvitesEnabled = usePlusOneInvites();

  const isCancelled = reservation.status === "cancelled";
  const isCantBeSeated = reservation.status === "cant_be_seated";
  // Use bookingTimes from API if available, otherwise fallback to localEventDate
  const eventDate = dayjs(
    reservation.bookingTimes?.eventTime || reservation.localEventDate,
  );
  const now = dayjs();

  const isAssigned = reservation.assignmentStatus === "confirmed";

  const tableReadyMessage = useMemo(() => {
    if (!isAssigned || !reservation.table?.id) return null;
    const messages =
      (t("reservation.tableReadyMessages", {
        returnObjects: true,
      } as Record<string, unknown>) as unknown as string[]) ?? [];
    if (messages.length === 0) return null;
    const id = String(reservation.table.id);
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = (hash * 31 + id.charCodeAt(i)) | 0;
    }
    return messages[Math.abs(hash) % messages.length];
  }, [isAssigned, reservation.table?.id, t]);

  const paymentPending = reservation.status === "pending";
  const hasFeedback = !!reservation.feedbackCompleted;
  const isConfirmed = !!reservation.confirmedAt;
  const isFreeForNeighborhood = reservation.entitlementType === "free";

  // Calculate hours until event for conditional button rendering
  const hoursUntilEvent = eventDate.diff(now, "hour");
  const isPastEvent = hoursUntilEvent < 0;
  const showConfirmButton =
    !isCancelled &&
    !isPastEvent &&
    !isConfirmed &&
    hoursUntilEvent < 48 &&
    reservation.status === "active";

  const allowEditing =
    !isCancelled &&
    !isPastEvent &&
    (reservation.bookingTimes
      ? now.isBefore(dayjs(reservation.bookingTimes.cancelableUntil))
      : hoursUntilEvent > CANCELLATION_REFUND_WINDOW_HOURS);

  // Track if we're within the cancellation period (no refund available)
  const withinCancellationPeriod =
    !isCancelled && !isPastEvent && !allowEditing;

  const showSurveyButton = isPastEvent && !isCancelled && isAssigned;

  const { data: tablemates = [], isLoading: tablematesLoading } = useTablemates(
    isAssigned ? reservation.id : null,
  );

  const getInitials = (firstName: string, lastName?: string | null) => {
    const first = firstName.charAt(0).toUpperCase();
    const last = lastName ? lastName.charAt(0).toUpperCase() : "";
    return `${first}${last}`;
  };

  const searchString = useSearch();
  const params = new URLSearchParams(searchString);
  const confirmParam = params.get("confirm") != null;
  const [autoOpenConfirmModal, setAutoOpenConfirmModal] = useState(
    confirmParam && allowAutoConfirm && showConfirmButton,
  );

  const specialRequests = useMemo(() => {
    if (!reservation.specialRequests) return null;
    const result = UserAPI.SpecialRequestsSchema.safeParse(
      JSON.parse(reservation.specialRequests),
    );
    if (result.success) {
      return result.data;
    }
    return null;
  }, [reservation.specialRequests]);

  const [timeUntilEvent, setTimeUntilEvent] = useState<string>("");

  useEffect(() => {
    const updateCountdown = () => {
      const now = dayjs();
      const diff = eventDate.diff(now);

      if (diff <= 0) {
        setTimeUntilEvent("");
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      let timeString = "";
      if (days > 0) timeString += `${days}d `;
      if (hours > 0) timeString += `${hours}h `;
      timeString += `${minutes}m`;

      setTimeUntilEvent(timeString);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [eventDate]);

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on menu or interactive elements
    if (
      !clickable ||
      (e.target as HTMLElement).closest("button") ||
      (e.target as HTMLElement).closest("a")
    ) {
      return;
    }
    navigate(`/booking/${reservation.id}`);
  };

  const handleConfirmReservation = useCallback(() => {
    confirmReservation.mutate(reservation.id, {
      onSuccess: () => {
        toast.success(t("reservation.confirmSuccess"));
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.error || t("reservation.confirmFailed"),
        );
      },
    });
  }, [confirmReservation, toast, reservation, t]);

  const handleCancelReservation = useCallback(() => {
    tracking.cancelDialogOpened(reservation.id, withinCancellationPeriod);
    setShowCancellationModal(true);
  }, [reservation.id, withinCancellationPeriod]);

  const handleCancellationConfirm = useCallback(
    (reason?: CancellationReason) => {
      setShowCancellationModal(false);
      tracking.cancelDialogConfirmed(reservation.id, withinCancellationPeriod);
      updateReservation.mutate(
        {
          id: reservation.id,
          data: {
            status: "cancelled" as const,
            ...(reason && { cancellationReason: reason }),
          },
        },
        {
          onSuccess: () => {
            toast.success(t("reservation.cancellation.cancelledSuccessfully"));
            onCancelSuccess?.();
          },
          onError: (error: any) => {
            toast.error(
              error?.response?.data?.error || t("reservation.cancellation.cancelFailed"),
            );
          },
        },
      );
    },
    [
      reservation.id,
      withinCancellationPeriod,
      updateReservation,
      onCancelSuccess,
      toast,
      t,
    ],
  );

  useEffect(() => {
    if (autoOpenConfirmModal) {
      handleConfirmReservation();
      setAutoOpenConfirmModal(false);
    }
  }, [autoOpenConfirmModal, setAutoOpenConfirmModal, handleConfirmReservation]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenu(false);
      }
      if (
        calendarMenuRef.current &&
        !calendarMenuRef.current.contains(event.target as Node)
      ) {
        setOpenCalendarMenu(false);
      }
    };

    if (openMenu || openCalendarMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [openMenu, openCalendarMenu]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      onClick={handleCardClick}
      className={`card overflow-hidden ${isCancelled || isCantBeSeated ? "opacity-75" : ""} ${
        clickable
          ? "cursor-pointer hover:border-primary-500/50 transition-colors"
          : ""
      }`}
    >
      <div className="flex-1 min-w-0">
        {/* Header: calendar tile + title */}
        {(() => {
          const parsed = parseUTCDate(reservation.eventTime.date);
          return (
            <div className="-mx-6 -mt-6 mb-4 px-4 pt-4 pb-4 rounded-t-xl flex items-center gap-4 border-b border-black/10 dark:border-white/5 bg-gradient-to-b from-white/60 to-white/25 dark:from-white/[0.04] dark:to-black/20">
              <DateBadge date={reservation.eventTime.date} size="xl" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <h3 className="font-semibold capitalize text-lg leading-tight break-words">
                    {reservation.neighborhood.name}
                  </h3>
                  {paymentPending && (
                    <span className="text-xs px-2 py-0.5 rounded bg-primary-500/20 text-primary-500 font-medium whitespace-nowrap">
                      {t("reservation.paymentPending")}
                    </span>
                  )}
                  {!paymentPending && (
                    <span
                      className={`text-xs px-2 py-0.5 text-center rounded font-medium whitespace-nowrap ${
                        isCantBeSeated
                          ? "bg-amber-900 text-amber-100"
                          : isCancelled
                            ? "bg-dark-muted/20 text-dark-muted"
                            : isAssigned
                              ? "bg-green-800 text-white"
                              : "bg-primary-700 text-white"
                      }`}
                    >
                      {isCantBeSeated
                        ? t("reservation.cantBeSeatedBadge")
                        : isPastEvent
                          ? t("common.status.completed")
                          : isCancelled
                            ? t("common.status.cancelled")
                            : isAssigned
                              ? t("reservation.tableAssigned")
                              : t("reservation.awaitingTable")}
                    </span>
                  )}
                  {isConfirmed && !isCancelled && !isPastEvent && (
                    <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-white font-medium whitespace-nowrap flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      {t("reservation.confirmed")}
                    </span>
                  )}
                  {isFreeForNeighborhood && !isCancelled && (
                    <span className="text-xs px-2 py-0.5 rounded bg-accent-500/20 text-accent-500 font-medium whitespace-nowrap">
                      {t("reservation.free")}
                    </span>
                  )}
                </div>
                <div className="text-sm text-dark-muted mt-1">
                  {getEventTypeEmoji(reservation.eventTime.type)}{" "}
                  {getEventTypeLabel(reservation.eventTime.type)} ·{" "}
                  {parsed.weekday} · {formatTimeToDisplay(reservation.eventTime.time)}
                </div>
              </div>
            </div>
          );
        })()}

          {/* Special Requests */}
          {expanded && specialRequests && Object.keys(specialRequests).length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
              {Object.entries(specialRequests).map(([key, value]) => {
                if (
                  value === null ||
                  value === undefined ||
                  value === "" ||
                  value === false
                )
                  return null;

                // Format the key to be human-readable
                const label = key
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())
                  .trim();

                // Handle boolean true values as compact tags
                if (typeof value === "boolean" && value === true) {
                  return (
                    <span
                      key={key}
                      className="inline-flex items-center gap-1 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-full font-medium"
                    >
                      <CheckCircle className="w-3 h-3" />
                      {label}
                    </span>
                  );
                }

                // Handle string/other values inline
                return (
                  <span
                    key={key}
                    className="bg-neutral-100 dark:bg-neutral-800 text-dark-muted px-2 py-0.5 rounded"
                  >
                    {key !== "specialRequestsText" && (
                      <span className="font-medium">{label}: </span>
                    )}
                    {String(value)}
                  </span>
                );
              })}
            </div>
          )}

          {/* Can't be seated apology */}
          {isCantBeSeated && (
            <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
              <div className="flex items-start gap-3">
                <Handshake className="w-5 h-5 text-amber-600 dark:text-amber-200 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-amber-600 dark:text-amber-200">
                    {t("reservation.cantBeSeatedTitle")}
                  </p>
                  <p className="text-sm text-dark-muted mt-1">
                    {t("reservation.cantBeSeatedMessage")}
                  </p>
                  <p className="text-sm text-dark-muted mt-2">
                    {t("reservation.cantBeSeatedRebook")}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Booking Stages */}
          {!isCancelled && !isCantBeSeated && !paymentPending && (
            <div className="mt-3">
              {/* Reservation Confirmed */}
              <div className="flex items-stretch gap-3">
                <div className="flex flex-col items-center shrink-0">
                  <div className="w-8 h-8 rounded-full bg-primary-500/15 flex items-center justify-center shrink-0">
                    <CheckCircle className="w-4 h-4 text-primary-500" />
                  </div>
                  <div className="flex-1 w-1 my-1.5 text-neutral-400/60 dark:text-neutral-500/60" style={{ backgroundImage: "radial-gradient(circle, currentColor 1.5px, transparent 1.5px)", backgroundSize: "4px 8px", backgroundRepeat: "repeat-y", backgroundPosition: "center top" }} />
                </div>
                <div className="flex-1 min-w-0 pt-1 pb-4">
                  <div className="text-sm font-semibold leading-tight text-primary-600 dark:text-primary-500">
                    {t("reservation.seatSaved")}
                  </div>
                  <div className="text-sm text-dark-muted mt-0.5">
                    {t("reservation.tableForming")}
                  </div>
                  {reservation.calendarLinks && !isPastEvent && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <div className="relative" ref={calendarMenuRef}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenCalendarMenu(!openCalendarMenu);
                          }}
                          className="btn-inline"
                        >
                          <CalendarPlus className="w-3.5 h-3.5 flex-shrink-0" />
                          {t("reservation.addToCalendar")}
                        </button>
                        {openCalendarMenu && (
                          <div className="absolute left-0 top-full mt-1 w-44 border border-content bg-white dark:bg-neutral-900 rounded-lg shadow-lg z-10 overflow-hidden">
                            <a
                              href={reservation.calendarLinks.googleCalendarUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full px-4 py-3 text-left text-sm flex items-center gap-2 hover:bg-primary-500/10 text-type-900 dark:text-white transition-colors"
                              onClick={(e: React.MouseEvent) =>
                                openCalendarLink(
                                  e,
                                  reservation.calendarLinks!.googleCalendarUrl,
                                  "google",
                                )
                              }
                            >
                              <CalendarPlus className="w-4 h-4 flex-shrink-0" />
                              {t("reservation.googleCalendar")}
                            </a>
                            <a
                              href={reservation.calendarLinks.icsUrl}
                              className="w-full px-4 py-3 text-left text-sm flex items-center gap-2 hover:bg-primary-500/10 text-type-900 dark:text-white transition-colors border-t border-divider"
                              onClick={(e: React.MouseEvent) =>
                                openCalendarLink(
                                  e,
                                  reservation.calendarLinks!.icsUrl,
                                  "ics",
                                )
                              }
                            >
                              <Download className="w-4 h-4 flex-shrink-0" />
                              {t("reservation.downloadIcs")}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Restaurant */}
              <div className="flex items-stretch gap-3">
                <div className="flex flex-col items-center shrink-0">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      isAssigned && reservation.table
                        ? "bg-primary-500/15"
                        : "bg-neutral-500/15"
                    }`}
                  >
                    <MapPin
                      className={`w-4 h-4 ${
                        isAssigned && reservation.table
                          ? "text-primary-500"
                          : "text-dark-muted"
                      }`}
                    />
                  </div>
                  <div className="flex-1 w-1 my-1.5 text-neutral-400/60 dark:text-neutral-500/60" style={{ backgroundImage: "radial-gradient(circle, currentColor 1.5px, transparent 1.5px)", backgroundSize: "4px 8px", backgroundRepeat: "repeat-y", backgroundPosition: "center top" }} />
                </div>
                <div className="flex-1 min-w-0 pt-1 pb-4">
                  <div
                    className={`text-sm font-semibold leading-tight ${
                      isAssigned && reservation.table
                        ? "text-primary-600 dark:text-primary-500"
                        : ""
                    }`}
                  >
                    {t("reservation.comingSoon")}
                  </div>
                  {isAssigned && reservation.table ? (
                    <>
                      <a
                        href={reservation.table.website || ""}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary-200 dark:hover:text-primary-500 inline-flex items-center gap-1 font-medium break-words mt-0.5"
                      >
                        <span className="break-words">
                          {reservation.table.restaurantName}
                        </span>
                        {reservation.table.website && (
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        )}
                      </a>

                      <div className="text-sm text-dark-muted mt-0.5 break-words">
                        {reservation.table.address}
                      </div>

                      {reservation.table.reservationName && (
                        <div className="flex flex-wrap items-center gap-1.5 text-sm text-dark-muted mt-1">
                          <Users className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="break-words">
                            {t("reservation.underName")}{" "}
                            <span className="font-bold">
                              {reservation.table.reservationName}
                            </span>
                          </span>
                        </div>
                      )}


                      <div className="mt-2 flex flex-wrap gap-2">
                        {reservation.table.googleMapsLink && (
                          <a
                            href={reservation.table.googleMapsLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-inline"
                            onClick={(e: React.MouseEvent) =>
                              e.stopPropagation()
                            }
                          >
                            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                            {t("reservation.getDirections")}
                          </a>
                        )}
                        {reservation.table.id && (
                          <Link
                            href={`/chat/table_${reservation.table.id}`}
                            className="btn-inline"
                            onClick={(e: React.MouseEvent) =>
                              e.stopPropagation()
                            }
                          >
                            <MessageCircle className="w-3.5 h-3.5 flex-shrink-0" />
                            {t("reservation.messageTable")}
                          </Link>
                        )}
                        {tableReadyMessage && (
                          <div className="text-sm text-dark-muted mt-0.5 mb-2 break-words">
                            {tableReadyMessage}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-dark-muted mt-0.5">
                      {t("reservation.restaurantDetailsSoon")}
                    </div>
                  )}
                </div>
              </div>

              {/* Close-out bar */}
              {reservation.closeOut && (
                <div className="flex items-stretch gap-3">
                  <div className="flex flex-col items-center shrink-0">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        reservation.closeOut.state === "open"
                          ? "bg-primary-500/15"
                          : "bg-neutral-500/15"
                      }`}
                    >
                      <Beer
                        className={`w-4 h-4 ${
                          reservation.closeOut.state === "open"
                            ? "text-primary-500"
                            : "text-dark-muted"
                        }`}
                      />
                    </div>
                    <div className="flex-1 w-1 my-1.5 text-neutral-400/60 dark:text-neutral-500/60" style={{ backgroundImage: "radial-gradient(circle, currentColor 1.5px, transparent 1.5px)", backgroundSize: "4px 8px", backgroundRepeat: "repeat-y", backgroundPosition: "center top" }} />
                  </div>
                  <div className="flex-1 min-w-0 pt-1 pb-4">
                    <div
                      className={`text-sm font-semibold leading-tight ${
                        reservation.closeOut.state === "open"
                          ? "text-primary-600 dark:text-primary-500"
                          : ""
                      }`}
                    >
                      {t("reservation.closeOutStage")}
                    </div>
                    <div className="text-sm text-dark-muted mt-0.5">
                      {reservation.closeOut.state === "open"
                        ? t("reservation.closeOutStageDescriptionOpen")
                        : t("reservation.closeOutStageDescription")}
                    </div>
                    {reservation.closeOut.state === "open" && reservation.closeOut.bar && (
                      <>
                        <a
                          href={reservation.closeOut.bar.website || ""}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-primary-200 dark:hover:text-primary-500 inline-flex items-center gap-1 font-medium break-words mt-2"
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                          <span className="break-words">
                            {reservation.closeOut.bar.name}
                          </span>
                          {reservation.closeOut.bar.website && (
                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          )}
                        </a>
                        {reservation.closeOut.bar.address && (
                          <div className="text-sm text-dark-muted mt-0.5 break-words">
                            {reservation.closeOut.bar.address}
                          </div>
                        )}
                      </>
                    )}
                    {reservation.closeOut.state === "open" && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {reservation.closeOut.bar?.googleMapsLink && (
                          <a
                            href={reservation.closeOut.bar.googleMapsLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-inline"
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          >
                            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                            {t("reservation.getDirections")}
                          </a>
                        )}
                        {reservation.closeOut.conversationId && (
                          <Link
                            href={`/chat/${reservation.closeOut.conversationId}`}
                            className="btn-inline"
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          >
                            <MessageCircle className="w-3.5 h-3.5 flex-shrink-0" />
                            {t("reservation.joinCloseOutChat")}
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Connections */}
              <div className="flex items-stretch gap-3">
                <div className="flex flex-col items-center shrink-0">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      showSurveyButton
                        ? "bg-primary-500/15"
                        : "bg-neutral-500/15"
                    }`}
                  >
                    <Handshake
                      className={`w-4 h-4 ${
                        showSurveyButton
                          ? "text-primary-500"
                          : "text-dark-muted"
                      }`}
                    />
                  </div>
                </div>
                <div className="flex-1 min-w-0 pt-1">
                  <div
                    className={`text-sm font-semibold leading-tight ${
                      showSurveyButton
                        ? "text-primary-600 dark:text-primary-500"
                        : ""
                    }`}
                  >
                    {t("reservation.connectStage")}
                  </div>
                  <div className="text-sm text-dark-muted mt-0.5">
                    {t("reservation.connectStageDescription")}
                  </div>

                  {showSurveyButton && (
                    <div className="mt-3">
                      <ConnectionsEditor bookingId={reservation.id} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Cancellation Info */}
          {isCancelled && reservation.updatedAt && (
            <p className="text-xs text-dark-muted mt-3">
              {t("reservation.cancelledOn")}{" "}
              {formatDate(new Date(reservation.updatedAt), DateFormats.short)}
            </p>
          )}

          {/* +1 invites — gated by PostHog feature flag; only on active,
              upcoming bookings. Accepting/sending happens here, plus a
              "going with X" chip whenever the booking is linked. The
              cancellation window comes from `allowEditing` so the +1
              connection lifts when the booking can no longer be changed. */}
          {!isPastEvent &&
            !isCancelled &&
            !isCantBeSeated &&
            reservation.status === "active" && (
              <InvitePlusOneSection
                bookingId={reservation.id}
                allowInviting={allowEditing && plusOneInvitesEnabled}
                eventType={reservation.eventTime.type}
              />
            )}

          {/* Confirmation Button */}
          {showConfirmButton && (
            <div className="mt-4 pt-4 border-t border-dark-border dark:border-neutral-700">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleConfirmReservation();
                }}
                disabled={confirmReservation.isPending}
                className="flex items-center justify-center gap-2 btn btn-primary w-full"
              >
                {confirmReservation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t("reservation.confirming")}
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {t("reservation.confirmAttendance")}
                  </>
                )}
              </button>
            </div>
          )}

          {/* Pre-dinner Tablemates */}
          {!isPastEvent &&
            !isCancelled &&
            isAssigned &&
            !tablematesLoading &&
            tablemates.length > 0 && (
              <div className="mt-4 pt-4 border-t border-divider space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-dark-muted">
                  <Users className="w-4 h-4" />
                  <span>{t("reservation.attending")}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {tablemates.map((tablemate) => (
                    <div
                      key={tablemate.id}
                      className="flex items-center gap-2 px-3 py-2 bg-neutral-100 dark:bg-neutral-800 rounded-full"
                    >
                      <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-sm font-semibold text-primary-700 dark:text-primary-300">
                        {getInitials(tablemate.firstName, tablemate.lastName)}
                      </div>
                      <span className="text-sm font-medium">
                        {tablemate.firstName}
                        {tablemate.lastName
                          ? ` ${tablemate.lastName.charAt(0).toUpperCase()}.`
                          : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Countdown Banner */}
          {timeUntilEvent && !isCancelled && !isCantBeSeated && !isPastEvent && (
            <div className="-mx-6 -mb-6 mt-6 pl-4 pr-2 py-2 rounded-b-xl border-t border-black/10 dark:border-white/5 bg-white/40 dark:bg-black/25 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Hourglass className="w-3.5 h-3.5 " />
                <span className="text-sm font-medium tracking-wider">
                  {t("reservation.inTime", { time: timeUntilEvent })}
                </span>
              </div>
              <div className="relative" ref={menuRef}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenu(!openMenu);
                  }}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 dark:bg-white/10 dark:hover:bg-white/15 transition-colors"
                >
                  {t("reservation.changeBooking")}
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform ${openMenu ? "rotate-180" : ""}`}
                  />
                </button>

                {openMenu && (
                  <div className="absolute right-0 bottom-full mb-2 w-56 rounded-2xl border border-content bg-white/85 dark:bg-neutral-900/85 backdrop-blur-xl shadow-[0_8px_24px_0_rgba(0,0,0,0.25)] z-10 overflow-hidden">
                    {allowEditing && (
                      <Link
                        href={`/booking?bookingId=${reservation.id}`}
                        className="w-full px-4 py-3 text-left text-sm flex items-center gap-2.5 hover:bg-black/5 dark:hover:bg-white/5 text-neutral-700 dark:text-neutral-200 transition-colors"
                        onClick={() => setOpenMenu(false)}
                      >
                        <Edit className="w-4 h-4 flex-shrink-0 text-dark-muted" />
                        {t("events.editReservation")}
                      </Link>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenu(false);
                        handleCancelReservation();
                      }}
                      disabled={updateReservation.isPending}
                      className={`w-full px-4 py-3 text-left text-sm flex items-center gap-2.5 text-neutral-700 dark:text-neutral-200 hover:bg-black/5 dark:hover:bg-white/5 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50 transition-colors ${allowEditing ? "border-t border-neutral-400/15 dark:border-neutral-500/15" : ""}`}
                    >
                      <X className="w-4 h-4 flex-shrink-0 text-dark-muted" />
                      {withinCancellationPeriod
                        ? t("reservation.cantAttend")
                        : t("events.cancelReservation")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Survey Footer */}
          {showSurveyButton && (
            <div className="-mx-6 -mb-6 mt-6 px-4 py-3 rounded-b-xl border-t border-black/10 dark:border-white/5 bg-white/40 dark:bg-black/25 flex items-center justify-center">
              <Link
                href={`/dinner-survey/${reservation.id}`}
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                  hasFeedback
                    ? "bg-green-500/15 text-green-700 dark:text-green-400 hover:bg-green-500/25"
                    : "bg-primary-500 text-white hover:bg-primary-600"
                }`}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                {hasFeedback ? (
                  <>
                    <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    {t("reservation.surveyCompletedEdit")}
                  </>
                ) : (
                  <>
                    <MessageCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    {t("reservation.completeSurvey")}
                  </>
                )}
              </Link>
            </div>
          )}
      </div>

      {/* Cancellation Modal */}
      <CancellationModal
        isOpen={showCancellationModal}
        isLateCancellation={withinCancellationPeriod}
        onConfirm={handleCancellationConfirm}
        onClose={() => setShowCancellationModal(false)}
      />
    </motion.div>
  );
}
